import type { Session } from "@supabase/supabase-js";

import { idempotencyKey, tinkrApiFetch } from "./client";

export type PlatformComponentStatus =
  | "normal"
  | "degraded"
  | "maintenance"
  | "incident";

export type PlatformStatusResponse = {
  ok: boolean;
  mode: PlatformComponentStatus;
  generatedAt: string;
  components: Array<{
    component: string;
    status: PlatformComponentStatus;
    severity: "info" | "warning" | "critical";
    updatedAt: string;
  }>;
  activeMaintenance: Array<{
    id: string;
    maintenanceType: "scheduled" | "emergency";
    status: "scheduled" | "active" | "completed" | "cancelled";
    affectedComponents: string[];
    writeGateEnabled: boolean;
    publicTitle: string;
    publicMessage: string;
    startsAt: string;
    endsAt: string | null;
  }>;
  scheduledMaintenance: Array<{
    id: string;
    maintenanceType: "scheduled" | "emergency";
    status: "scheduled" | "active" | "completed" | "cancelled";
    affectedComponents: string[];
    writeGateEnabled: boolean;
    publicTitle: string;
    publicMessage: string;
    startsAt: string;
    endsAt: string | null;
  }>;
  actionBlocks: Array<{
    code: "writes_blocked";
    components: string[];
    reason: "maintenance";
  }>;
};

export type OperationalAnnouncement = {
  id: string;
  status: string;
  audiences: string[];
  severity: "info" | "warning" | "critical";
  displayMode: "banner" | "modal" | "inbox";
  requiresAck: boolean;
  title: string;
  body: string;
  locale: string;
  startsAt: string;
  endsAt: string | null;
  seenAt: string | null;
  acknowledgedAt: string | null;
};

export type OperationalAnnouncementsResponse = {
  ok: true;
  announcements: OperationalAnnouncement[];
};

export type AcknowledgeAnnouncementResponse = {
  ok: true;
  announcementId: string;
  acknowledgedAt: string | null;
};

export function getPlatformStatus(
  session: Session
): Promise<PlatformStatusResponse> {
  return tinkrApiFetch<PlatformStatusResponse>(session, "/v1/platform/status");
}

export function listProviderAnnouncements(
  session: Session,
  locale: string
): Promise<OperationalAnnouncementsResponse> {
  const params = new URLSearchParams({ locale });
  return tinkrApiFetch<OperationalAnnouncementsResponse>(
    session,
    `/v1/provider/announcements?${params.toString()}`
  );
}

export function acknowledgeProviderAnnouncement(
  session: Session,
  announcementId: string
): Promise<AcknowledgeAnnouncementResponse> {
  return tinkrApiFetch<AcknowledgeAnnouncementResponse>(
    session,
    `/v1/provider/announcements/${encodeURIComponent(announcementId)}/ack`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-announcement-ack")
      }
    }
  );
}
